import i18n, { t } from "i18next";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { MagicStar, Profile2User } from "iconsax-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import DBButton from "../../component/global/DBButton";
import DBModal from "../../component/global/DBModal";
import DBTable from "../../component/global/DBTable/DBTable";
import DBPagination from "../../component/global/DBPagination";
import DBSpinner from "../../component/global/DBSpinner";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import { surveyActionUrl } from "../../helperData/ApiAddress";
import RowActionButton from "./RowActionButton";
import { campaignKindLabel } from "./constants";
import {
    isCsvContentType,
    nextExportPollAction,
    resolveExportJobId,
} from "./exportHelpers";
import { translateServerDetail } from "./translateServerDetail";
import { formatSurveyDateTime } from "./constants";
import { AiCoinHint, isInsufficientCoins, useAiCoinGate } from "../../features/ai-billing/AiCoinGate";
import AiInsightBody from "../../features/ai-insight/AiInsightBody";

function questionScore(question) {
    const states = (question.states || []).filter((state) => {
        const value = Number(state.value);
        return state.value != null && state.value !== "" && !Number.isNaN(value);
    });
    const answers = states.reduce((sum, state) => sum + (state.count || 0), 0);
    if (!answers) return null;
    const total = states.reduce(
        (sum, state) => sum + Number(state.value) * (state.count || 0),
        0
    );
    return {
        name: question.name,
        average: total / answers,
        answers,
        max: Math.max(...states.map((state) => Number(state.value))),
    };
}

function overallScore(questions) {
    const scored = (questions || []).map(questionScore).filter(Boolean);
    const answers = scored.reduce((sum, row) => sum + row.answers, 0);
    if (!answers) return null;
    const total = scored.reduce((sum, row) => sum + row.average * row.answers, 0);
    return {
        rows: scored.map((row) => ({
            name: row.name,
            average: Number(row.average.toFixed(2)),
        })),
        average: total / answers,
        max: Math.max(...scored.map((row) => row.max)),
    };
}

function formatScore(value) {
    return Number(value).toLocaleString(
        i18n.language?.startsWith("fa") ? "fa-IR" : "en-US",
        { maximumFractionDigits: 1 }
    );
}

function ParticipantBody({ response }) {
    if (!response) return null;
    const contact = response.participant || {};
    const contactRows = [
        ["name", contact.name],
        ["phone", contact.phone],
        ["email", contact.email],
    ].filter(([, value]) => value);
    const features = response.feature_replies || [];
    if (!contactRows.length && !features.length) {
        return <p>{t("survey.results.participant.empty")}</p>;
    }
    return (
        <dl className="survey-participant-details">
            {contactRows.map(([key, value]) => (
                <div key={key}>
                    <dt>{t(`survey.results.participant.${key}`)}</dt>
                    <dd>{value}</dd>
                </div>
            ))}
            {features.map((reply) => (
                <div key={reply.id}>
                    <dt>{reply.name}</dt>
                    <dd>{reply.value || "—"}</dd>
                </div>
            ))}
        </dl>
    );
}

export default function ResultsPanel({ survey }) {
    const toast = useContext(ToastContext);
    const [analytics, setAnalytics] = useState(null);
    const [responses, setResponses] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [exportLoading, setExportLoading] = useState(false);
    const [aiSummary, setAiSummary] = useState(null);
    const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
    const [participant, setParticipant] = useState(null);

    const [getAnalytics, getAnalyticsLoading] = useRequest(
        surveyActionUrl(survey.id, "analytics"),
        "GET"
    );
    const [getResponses, getResponsesLoading] = useRequest(
        surveyActionUrl(survey.id, "responses"),
        "GET"
    );
    const [getAiSummary] = useRequest(
        surveyActionUrl(survey.id, "ai-summary"),
        "GET"
    );
    const [generateAiSummary, generateAiSummaryLoading] = useRequest(
        surveyActionUrl(survey.id, "ai-summary"),
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    const authHeaders = () => ({
        Authorization: `Bearer ${sessionStorage.getItem("auth")}`,
    });

    useEffect(() => {
        getAnalytics().then(setAnalytics).catch(() => setAnalytics(null));
        getAiSummary()
            .then(setAiSummary)
            .catch(() => setAiSummary(null));
    }, [survey.id]);

    useEffect(() => {
        getResponses({ page: currentPage })
            .then((r) => {
                setResponses(r.results || []);
                setTotalCount(r.count || 0);
            })
            .catch(() => setResponses([]));
    }, [survey.id, currentPage]);

    const downloadExport = (jobId, attempt = 0) => {
        axios
            .get(`${surveyActionUrl(survey.id, "export")}${jobId}/`, {
                headers: authHeaders(),
                responseType: "blob",
            })
            .then((r) => {
                const contentType = r.headers?.["content-type"] || "";
                if (isCsvContentType(contentType)) {
                    const url = URL.createObjectURL(r.data);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `survey-${survey.id}-responses.csv`;
                    link.click();
                    URL.revokeObjectURL(url);
                    setExportLoading(false);
                    return;
                }
                r.data.text().then((text) => {
                    let statusValue;
                    try {
                        statusValue = JSON.parse(text).status;
                    } catch {
                        setExportLoading(false);
                        toast(t("survey.results.export.error"), "danger");
                        return;
                    }
                    if (nextExportPollAction(statusValue, attempt) === "poll") {
                        setTimeout(
                            () => downloadExport(jobId, attempt + 1),
                            3000
                        );
                        return;
                    }
                    setExportLoading(false);
                    toast(t("survey.results.export.error"), "danger");
                });
            })
            .catch(() => {
                setExportLoading(false);
                toast(t("survey.results.export.error"), "danger");
            });
    };

    const handleExport = () => {
        setExportLoading(true);
        axios
            .post(
                surveyActionUrl(survey.id, "export"),
                {},
                { headers: authHeaders() }
            )
            .then((r) => {
                const jobId = resolveExportJobId(r);
                if (!jobId) {
                    setExportLoading(false);
                    toast(t("survey.results.export.error"), "danger");
                    return;
                }
                toast(t("survey.results.export.started"), "info");
                downloadExport(jobId);
            })
            .catch(() => {
                setExportLoading(false);
                toast(t("survey.results.export.error"), "danger");
            });
    };

    const handleGenerateAiSummary = () => {
        confirmAndRun("survey_summary", () => {
            setAiSummaryLoading(true);
            generateAiSummary()
                .then(setAiSummary)
                .catch((e) => {
                    if (isInsufficientCoins(e)) {
                        openFromError(e);
                        return;
                    }
                    const detail = translateServerDetail(
                        e.response?.data?.detail,
                        t
                    );
                    toast(detail || t("survey.results.ai.error"), "danger");
                })
                .finally(() => setAiSummaryLoading(false));
        });
    };

    const campaignHeaders = [
        {
            id: "name",
            title: t("survey.campaign.table.name"),
            gridTemplateColumns: "1.4fr",
        },
        {
            id: "kind",
            title: t("survey.campaign.table.kind"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "sent",
            title: t("survey.campaign.table.sent"),
            gridTemplateColumns: "0.8fr",
        },
        {
            id: "started",
            title: t("survey.results.started"),
            gridTemplateColumns: "0.8fr",
        },
        {
            id: "completed",
            title: t("survey.results.completed"),
            gridTemplateColumns: "0.8fr",
        },
        {
            id: "failed",
            title: t("survey.campaign.table.failed"),
            gridTemplateColumns: "0.7fr",
        },
    ];

    const responseHeaders = [
        {
            id: "campaign",
            title: t("survey.results.table.campaign"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "submitted",
            title: t("survey.results.table.submitted"),
            gridTemplateColumns: "1fr",
        },
        {
            id: "details",
            title: t("survey.results.table.details"),
            gridTemplateColumns: "3fr",
        },
        {
            id: "participant",
            title: t("survey.results.table.participant"),
            gridTemplateColumns: "72px",
        },
    ];

    const responseRows = responses.map((response) => ({
        campaign: response.campaign_name || "—",
        submitted: formatSurveyDateTime(
            response.jalali_submitted_at || response.submitted_at
        ),
        details: (
            <ul className="survey-response-details">
                {(response.criteria_replies || []).map((reply) => (
                    <li key={reply.id}>
                        <strong>{reply.question}:</strong>{" "}
                        {reply.answer ||
                            reply.description ||
                            (reply.file ? t("survey.response.file.attach") : "—")}
                        {reply.description && reply.answer
                            ? ` — ${reply.description}`
                            : ""}
                    </li>
                ))}
            </ul>
        ),
        participant: (
            <div className="survey-row-actions">
                <RowActionButton
                    icon={Profile2User}
                    label={t("survey.results.participant.open")}
                    onClick={() => setParticipant(response)}
                />
            </div>
        ),
    }));

    const score = overallScore(analytics?.questions);
    const completedCount =
        aiSummary?.completed ?? analytics?.totals?.completed ?? 0;
    const aiEnabled = aiSummary?.ai_enabled ?? survey.ai_enabled;
    const hasAiText = Boolean(aiSummary?.summary || aiSummary?.headline);
    const aiGeneratedAt =
        aiSummary?.jalali_generated_at || aiSummary?.generated_at;

    if (getAnalyticsLoading) {
        return <DBSpinner tip={t("loading")} />;
    }

    return (
        <>
        <div className="results-panel">
            <section className="survey-ai-summary" aria-labelledby="survey-ai-summary-title">
                <div className="survey-ai-summary-header">
                    <h4 id="survey-ai-summary-title">
                        <MagicStar size={18} aria-hidden="true" />
                        {t("survey.results.ai.title")}
                    </h4>
                    {aiEnabled && completedCount > 0 && (
                        <DBButton
                            type="secondary"
                            outlined
                            loading={aiSummaryLoading || generateAiSummaryLoading}
                            caption={
                                <>
                                    {hasAiText
                                        ? t("survey.results.ai.refresh")
                                        : t("survey.results.ai.generate")}
                                    <AiCoinHint operation="survey_summary" />
                                </>
                            }
                            onClick={handleGenerateAiSummary}
                        />
                    )}
                </div>
                {!aiEnabled ? (
                    <AiInsightBody tone="muted">
                        <p className="ai-insight__summary">
                            {t("survey.results.ai.disabled")}
                        </p>
                    </AiInsightBody>
                ) : hasAiText ? (
                    <AiInsightBody
                        headline={aiSummary.headline}
                        summary={aiSummary.summary}
                        highlights={aiSummary.highlights}
                        metaItems={
                            aiGeneratedAt
                                ? [
                                      {
                                          id: "generated",
                                          text: t(
                                              "survey.results.ai.generated_at",
                                              {
                                                  date: formatSurveyDateTime(
                                                      aiGeneratedAt
                                                  ),
                                              }
                                          ),
                                      },
                                  ]
                                : []
                        }
                    />
                ) : (
                    <AiInsightBody tone="empty">
                        <p className="ai-insight__summary">
                            {t("survey.results.ai.empty")}
                        </p>
                    </AiInsightBody>
                )}
            </section>

            <div className="survey-stats-row">
                <div className="survey-stat-card">
                    <span>{t("survey.results.started")}</span>
                    <strong>{analytics?.totals?.started ?? 0}</strong>
                </div>
                <div className="survey-stat-card">
                    <span>{t("survey.results.completed")}</span>
                    <strong>{analytics?.totals?.completed ?? 0}</strong>
                </div>
                <div className="survey-stat-card">
                    <span>{t("survey.results.completion.rate")}</span>
                    <strong>{analytics?.totals?.completion_rate ?? 0}%</strong>
                </div>
                <DBButton
                    type="secondary"
                    outlined
                    loading={exportLoading}
                    caption={t("survey.results.export")}
                    onClick={handleExport}
                />
            </div>

            {analytics?.campaigns?.length > 0 && (
                <>
                    <h4>{t("survey.results.per.campaign")}</h4>
                    <div className="surveys-table-wrap">
                        <DBTable
                            headers={campaignHeaders}
                            rows={analytics.campaigns.map((campaign) => ({
                                name: campaign.name,
                                kind: campaignKindLabel(
                                    campaign.kind,
                                    campaign.user_group_type
                                ),
                                sent: campaign.sent,
                                started: campaign.started,
                                completed: campaign.completed,
                                failed: campaign.failed,
                            }))}
                        />
                    </div>
                </>
            )}

            <h4>{t("survey.results.per.question")}</h4>
            {!analytics?.questions?.length ||
            analytics.questions.every((question) =>
                question.states.every((state) => state.count === 0)
            ) ? (
                <div className="surveys-empty">{t("survey.results.empty")}</div>
            ) : (
                <div className="survey-question-charts">
                    {score ? (
                        <div className="survey-question-chart survey-overall-chart">
                            <h5>
                                {t("survey.results.overall")}
                                <strong>
                                    {t("survey.results.overall.scale", {
                                        score: formatScore(score.average),
                                        max: formatScore(score.max),
                                    })}
                                </strong>
                            </h5>
                            <ResponsiveContainer
                                width="100%"
                                height={Math.max(180, score.rows.length * 48)}
                            >
                                <BarChart
                                    data={score.rows}
                                    layout="vertical"
                                    margin={{
                                        top: 8,
                                        right: 16,
                                        left: 8,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        domain={[0, score.max]}
                                        allowDecimals
                                    />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={180}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip
                                        formatter={(value) => [
                                            formatScore(value),
                                            t("survey.results.overall.axis"),
                                        ]}
                                    />
                                    <ReferenceLine
                                        x={Number(score.average.toFixed(2))}
                                        stroke="#0f766e"
                                        strokeDasharray="4 4"
                                    />
                                    <Bar
                                        dataKey="average"
                                        fill="#0f766e"
                                        name={t("survey.results.overall.axis")}
                                        barSize={18}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : null}
                    {analytics.questions.map((question) => (
                        <div
                            className="survey-question-chart"
                            key={question.criteria_info_id}
                        >
                            <h5>{question.name}</h5>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart
                                    data={question.states}
                                    margin={{
                                        top: 10,
                                        right: 10,
                                        left: -20,
                                        bottom: 0,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#2E71FC" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ))}
                </div>
            )}

            <h4>{t("survey.results.responses")}</h4>
            {getResponsesLoading ? (
                <DBSpinner tip={t("loading")} />
            ) : responses.length === 0 ? (
                <div className="surveys-empty">{t("survey.results.empty")}</div>
            ) : (
                <>
                    <div className="surveys-table-wrap">
                        <DBTable
                            headers={responseHeaders}
                            rows={responseRows}
                        />
                    </div>
                    <DBPagination
                        current={currentPage}
                        total={totalCount}
                        onChange={(page) => setCurrentPage(page)}
                    />
                </>
            )}
        </div>
        {gate}
        <DBModal
            show={Boolean(participant)}
            onHide={() => setParticipant(null)}
            minWidth={420}
            ariaLabelledby="survey-participant-title"
        >
            <DBModal.Header
                closeBtn={() => setParticipant(null)}
                title={
                    <span id="survey-participant-title">
                        {t("survey.results.participant.title")}
                    </span>
                }
            />
            <DBModal.Content>
                <ParticipantBody response={participant} />
            </DBModal.Content>
        </DBModal>
        </>
    );
}
